import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy } from 'firebase/firestore';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebaseConfig';

function AdminDishManagement() {
  const [dishes, setDishes] = useState([]);
  const [sedes, setSedes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const [dishName, setDishName] = useState('');
  const [dishDescription, setDishDescription] = useState('');
  const [dishPrices, setDishPrices] = useState({}); // Changed from dishPrice to dishPrices
  const [dishImageUrl, setDishImageUrl] = useState('');
  const [imageFile, setImageFile] = useState(null); // New state for the actual image file
  const [uploadProgress, setUploadProgress] = useState(0); // New state for upload progress
  const [isUploading, setIsUploading] = useState(false); // New state to track upload status
  const [selectedCategoryName, setSelectedCategoryName] = useState('');
  const [selectedSedeIds, setSelectedSedeIds] = useState([]);
  const [editingDish, setEditingDish] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      // Fetch Sedes
      const sedesCollection = collection(db, 'menuSedes');
      const sedesSnapshot = await getDocs(query(sedesCollection, orderBy('name')));
      const sedesList = sedesSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setSedes(sedesList);

      // Fetch Categories (assuming categories are consistent across sedes for now)
      // For simplicity, we'll just get categories from the first sede if available
      let categoriesList = [];
      if (sedesList.length > 0) {
        const firstSedeId = sedesList[0].id;
        const categoriesCollection = collection(db, 'menuSedes', firstSedeId, 'categories');
        const categoriesSnapshot = await getDocs(query(categoriesCollection, orderBy('name')));
        categoriesList = categoriesSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        const uniqueCategories = Array.from(new Map(categoriesList.map(item => [item.id, item])).values());
        setCategories(uniqueCategories);
      }

      // Fetch Dishes
      const dishesCollection = collection(db, 'dishes');
      const dishesSnapshot = await getDocs(query(dishesCollection, orderBy('name')));
      const dishesList = dishesSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setDishes(dishesList);

      setLoading(false);
    };
    fetchData();
  }, []);

  const handleImageUrlChange = (e) => {
    setDishImageUrl(e.target.value);
  };

  const handleSedeSelection = (sedeId) => {
    setSelectedSedeIds(prevSelectedSedes => {
      const newSelectedSedes = prevSelectedSedes.includes(sedeId)
        ? prevSelectedSedes.filter(id => id !== sedeId)
        : [...prevSelectedSedes, sedeId];

      // Also update dishPrices based on selection
      setDishPrices(prevPrices => {
        const newPrices = { ...prevPrices };
        if (newSelectedSedes.includes(sedeId)) {
          // If sede is selected, ensure it has a price entry (default to 0 if not already set)
          if (newPrices[sedeId] === undefined) {
            newPrices[sedeId] = 0;
          }
        } else {
          // If sede is deselected, remove its price entry
          delete newPrices[sedeId];
        }
        return newPrices;
      });

      return newSelectedSedes;
    });
  };

  const handlePriceChange = (sedeId, value) => {
    setDishPrices(prevPrices => ({
      ...prevPrices,
      [sedeId]: parseFloat(value) || 0 // Store as number, default to 0 if invalid
    }));
  };

  const handleFileChange = (e) => {
    if (e.target.files[0]) {
      setImageFile(e.target.files[0]);
      setDishImageUrl(''); // Clear previous URL if a new file is selected
    }
  };

  const handleImageUpload = async () => {
    if (!imageFile) return ''; // Return empty string if no file

    setIsUploading(true);
    const storageRef = ref(storage, `dish_images/${imageFile.name}_${Date.now()}`);
    const uploadTask = uploadBytesResumable(storageRef, imageFile);

    return new Promise((resolve, reject) => {
      uploadTask.on('state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
        },
        (error) => {
          console.error("Error uploading image:", error);
          setIsUploading(false);
          alert("Error al subir la imagen.");
          reject('');
        },
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          setIsUploading(false);
          setUploadProgress(0);
          setImageFile(null); // Clear the file input
          resolve(downloadURL);
        }
      );
    });
  };

  const resetForm = () => {
    setDishName('');
    setDishDescription('');
    setDishPrices({}); // Reset dishPrices
    setDishImageUrl(''); // Reset to empty string
    setImageFile(null);
    setUploadProgress(0);
    setIsUploading(false);
    setSelectedCategoryName('');
    setSelectedSedeIds([]);
    setEditingDish(null);
  };

  const handleAddOrUpdateDish = async () => {
    // Validate dish name, description, category, and at least one sede selected
    if (!dishName || !dishDescription || !selectedCategoryName || selectedSedeIds.length === 0) {
      alert("Por favor, complete el nombre, la descripción, la categoría y seleccione al menos una sede.");
      return;
    }

    // Ensure dishPrices is fully populated for all selectedSedeIds before validation
    const finalDishPrices = {};
    selectedSedeIds.forEach(sedeId => {
      finalDishPrices[sedeId] = dishPrices[sedeId] !== undefined ? dishPrices[sedeId] : 0;
    });

    // Validate that at least one price is entered for selected sedes
    const hasValidPrices = selectedSedeIds.every(sedeId => {
      const price = finalDishPrices[sedeId];
      return typeof price === 'number' && !isNaN(price) && price >= 0;
    });

    if (!hasValidPrices) {
      alert("Por favor, ingrese un precio válido para cada sede seleccionada.");
      return;
    }

    setLoading(true);

    try {
      let finalImageUrl = dishImageUrl; // Start with existing URL or manually entered URL

      if (imageFile) {
        // If a new image file is selected, upload it
        finalImageUrl = await handleImageUpload();
        if (!finalImageUrl) {
          // If upload failed, stop the process
          setLoading(false);
          return;
        }
      }

      const dishData = {
        name: dishName,
        description: dishDescription,
        prices: finalDishPrices, // Use the finalDishPrices here
        imageUrl: finalImageUrl, // Use the final image URL
        categoryName: selectedCategoryName,
        availableSedes: selectedSedeIds,
      };

      if (editingDish) {
        const dishRef = doc(db, 'dishes', editingDish.id);
        await updateDoc(dishRef, dishData);
        setDishes(dishes.map(dish => (dish.id === editingDish.id ? { ...dish, ...dishData } : dish)));
      } else {
        const docRef = await addDoc(collection(db, 'dishes'), dishData);
        setDishes([...dishes, { id: docRef.id, ...dishData }]);
      }
      resetForm();
    } catch (error) {
      console.error("Error adding/updating dish: ", error);
      alert("Error al guardar el plato. Verifique la consola para más detalles.");
    } finally {
      setLoading(false);
    }
  };

  const handleEditDish = (dish) => {
    setEditingDish(dish);
    setDishName(dish.name);
    setDishDescription(dish.description);
    setDishImageUrl(dish.imageUrl || ''); // Populate image URL
    setSelectedCategoryName(dish.categoryName);

    const availableSedes = dish.availableSedes || [];
    setSelectedSedeIds(availableSedes);

    // Initialize dishPrices based on existing prices and available sedes
    const initialDishPrices = {};
    availableSedes.forEach(sedeId => {
      if (dish.prices && dish.prices[sedeId] !== undefined) {
        initialDishPrices[sedeId] = dish.prices[sedeId];
      } else {
        initialDishPrices[sedeId] = 0; // Default to 0 if no price exists for this sede
      }
    });
    setDishPrices(initialDishPrices);
  };

  const handleDeleteDish = async (dishId) => {
    if (window.confirm("¿Estás seguro de que quieres eliminar este plato?")) {
      try {
        await deleteDoc(doc(db, 'dishes', dishId));
        setDishes(dishes.filter(dish => dish.id !== dishId));
      } catch (error) {
        console.error("Error deleting dish: ", error);
        alert("Error al eliminar el plato. Verifique la consola para más detalles.");
      }
    }
  };

  

  if (loading) {
    return <p>Cargando administración de platos...</p>;
  }

  return (
    <div>
      <h2>Administrar Platos del Menú</h2>

      {/* Dish Form */}
      <div style={{ marginBottom: '30px', padding: '20px', border: '1px solid #ccc', borderRadius: '8px' }}>
        <h3>{editingDish ? 'Editar Plato' : 'Agregar Nuevo Plato'}</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
          <input
            type="text"
            placeholder="Nombre del Plato"
            value={dishName}
            onChange={(e) => setDishName(e.target.value)}
            style={{ padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }}
          />
          <input
            type="text"
            placeholder="Descripción del Plato"
            value={dishDescription}
            onChange={(e) => setDishDescription(e.target.value)}
            style={{ padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }}
          />
          <div style={{ marginBottom: '15px' }}>
            <h4>Precios por Sede:</h4>
            {sedes.map(sede => (
              <div key={sede.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '5px' }}>
                <label>{sede.name}:</label>
                <input
                  type="number"
                  placeholder={`Precio en ${sede.name}`}
                  value={dishPrices[sede.id] || ''}
                  onChange={(e) => handlePriceChange(sede.id, e.target.value)}
                  style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd', flexGrow: 1 }}
                />
              </div>
            ))}
          </div>
          <div>
            <input
              type="file"
              onChange={handleFileChange}
              style={{ padding: '10px', borderRadius: '4px', border: '1px solid #ddd', width: '100%' }}
            />
            {isUploading && <p>Subiendo imagen: {uploadProgress.toFixed(2)}%</p>}
            {dishImageUrl && !isUploading && (
              <p style={{ fontSize: '0.8em', color: '#666', marginTop: '5px' }}>
                URL actual: <a href={dishImageUrl} target="_blank" rel="noopener noreferrer">{dishImageUrl.substring(0, 50)}{dishImageUrl.length > 50 ? '...' : ''}</a>
              </p>
            )}
          </div>
        </div>
        <div style={{ marginBottom: '15px' }}>
          <h4>Seleccionar Categoría:</h4>
          <select
            value={selectedCategoryName}
            onChange={(e) => setSelectedCategoryName(e.target.value)}
            style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd' }}
          >
            <option value="">-- Seleccionar Categoría --</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.name}>{cat.name}</option>
            ))}
          </select>
        </div>
        <div style={{ marginBottom: '15px' }}>
          <h4>Sedes Disponibles:</h4>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
            {sedes.map(sede => (
              <label key={sede.id} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <input
                  type="checkbox"
                  checked={selectedSedeIds.includes(sede.id)}
                  onChange={() => handleSedeSelection(sede.id)}
                />
                {sede.name}
              </label>
            ))}
          </div>
        </div>
        <button onClick={handleAddOrUpdateDish} disabled={isUploading} style={{ padding: '10px 20px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '5px', cursor: isUploading ? 'not-allowed' : 'pointer', marginRight: '10px' }}>
          {editingDish ? 'Actualizar Plato' : 'Agregar Plato'}
        </button>
        {editingDish && (
          <button onClick={resetForm} style={{ padding: '10px 20px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>
            Cancelar Edición
          </button>
        )}
      </div>

      {/* Dish List */}
      <h3>Platos Existentes</h3>
      
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '20px' }}>
        {dishes.map(dish => (
          <div key={dish.id} style={{ border: '1px solid #eee', borderRadius: '8px', padding: '15px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
            {dish.imageUrl && <img src={dish.imageUrl} alt={dish.name} style={{ width: '100%', height: '150px', objectFit: 'cover', borderRadius: '4px', marginBottom: '10px' }} />}
            <h4>{dish.name}</h4>
            <p>{dish.description}</p>
            <p><strong>Precios:</strong></p>
            <ul>
              {dish.prices && Object.entries(dish.prices).map(([sedeId, price]) => {
                const sedeName = sedes.find(s => s.id === sedeId)?.name || sedeId;
                return <li key={sedeId}>{sedeName}: ${price}</li>;
              })}
            </ul>
            <p><strong>Categoría:</strong> {dish.categoryName}</p>
            <p><strong>Sedes:</strong> {sedes.filter(sede => dish.availableSedes.includes(sede.id)).map(sede => sede.name).join(', ')}</p>
            <div style={{ marginTop: '10px' }}>
              <button onClick={() => handleEditDish(dish)} style={{ padding: '8px 12px', backgroundColor: '#ffc107', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', marginRight: '10px' }}>Editar</button>
              <button onClick={() => handleDeleteDish(dish.id)} style={{ padding: '8px 12px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Eliminar</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default AdminDishManagement;